/* eslint-disable react/prop-types */
/**
 * Phase 19 — multi-agent timeline panel (WS-E result panel,
 * shipped in CP4 for the preview).
 *
 * One horizontal lane per battery; each engagement renders as a
 * coloured bar along the time axis (start = launch, end = resolved).
 * Successes are filled; failures (Pk roll missed) are outlined only.
 *
 * Pure SVG — no new chart libs. Defence palette; UK spelling.
 */
import { useMemo } from "react";
import { DEFENCE_PALETTE } from "@/components/phase19/placementUtils";

export default function MultiAgentTimeline({
    engagements = [],
    scenarioClockS = 300,
    width = 460,
    height = 170,
}) {
    const lanes = useMemo(() => {
        const grouped = {};
        engagements.forEach((e) => {
            const k = String(e.battery_id || "unknown");
            grouped[k] = grouped[k] || [];
            grouped[k].push(e);
        });
        return Object.entries(grouped)
            .map(([battery_id, items]) => ({
                battery_id,
                items,
            }))
            .sort((a, b) => a.battery_id.localeCompare(b.battery_id));
    }, [engagements]);

    const ML = 72;
    const MR = 10;
    const MT = 12;
    const MB = 22;
    const plotW = width - ML - MR;
    const plotH = height - MT - MB;
    const laneH = lanes.length > 0 ? plotH / lanes.length : plotH;
    const tToPx = (t) =>
        ML + Math.max(0, Math.min(1, t / scenarioClockS)) * plotW;

    return (
        <figure
            className="flex flex-col gap-1"
            data-testid="multi-agent-timeline"
        >
            <figcaption
                className="flex items-baseline justify-between text-[11px] uppercase tracking-wide"
                style={{ color: DEFENCE_PALETTE.navy }}
            >
                <span>Multi-agent engagement timeline</span>
                <span
                    style={{
                        color: DEFENCE_PALETTE.steelGray,
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    {engagements.length} engagement{engagements.length === 1 ? "" : "s"}
                </span>
            </figcaption>
            <svg
                width={width}
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                style={{
                    fontVariantNumeric: "tabular-nums",
                    background: "#FFFFFF",
                    border: `1px solid ${DEFENCE_PALETTE.steelGray}`,
                    borderRadius: 4,
                }}
            >
                {/* lane backgrounds + labels */}
                {lanes.map((lane, i) => (
                    <g key={lane.battery_id}>
                        <rect
                            x={ML}
                            y={MT + i * laneH}
                            width={plotW}
                            height={laneH - 2}
                            fill={i % 2 === 0 ? "#F2F5F8" : "#FFFFFF"}
                        />
                        <text
                            x={ML - 6}
                            y={MT + i * laneH + laneH / 2 + 3}
                            fontSize={9}
                            textAnchor="end"
                            fill={DEFENCE_PALETTE.navy}
                        >
                            {lane.battery_id}
                        </text>
                        {lane.items.map((e, j) => {
                            // Synthesise time-of-fire from index (engine
                            // doesn't ship per-engagement timestamps in
                            // CP4's per-sample shape; CP5 brings them in).
                            const ratio = (j + 1) / (lane.items.length + 1);
                            const t = ratio * scenarioClockS;
                            return (
                                <g key={`${lane.battery_id}-${j}`}>
                                    <rect
                                        x={tToPx(t) - 4}
                                        y={MT + i * laneH + 4}
                                        width={8}
                                        height={laneH - 10}
                                        rx={1.5}
                                        fill={
                                            e.success
                                                ? DEFENCE_PALETTE.teal
                                                : "transparent"
                                        }
                                        stroke={DEFENCE_PALETTE.teal}
                                        strokeWidth={1.2}
                                        data-testid={`timeline-engagement-${lane.battery_id}-${j}`}
                                    />
                                </g>
                            );
                        })}
                    </g>
                ))}
                {/* x-axis ticks */}
                {[0, 0.25, 0.5, 0.75, 1].map((r) => (
                    <text
                        key={r}
                        x={ML + r * plotW}
                        y={height - 4}
                        fontSize={9}
                        textAnchor="middle"
                        fill={DEFENCE_PALETTE.steelGray}
                    >
                        {(r * scenarioClockS).toFixed(0)}s
                    </text>
                ))}
            </svg>
        </figure>
    );
}
